import { atom, useAtom, useAtomValue } from 'jotai';
import { secureHttpClient } from '../restClient';
import {
  Nameable,
  useGetUserPermissions,
  useGetUserProfile,
} from './userManager';
import { addToContext } from './contextManager';

type GetStoresResponse = {
  defaultStore: Nameable;
  stores: Nameable[] | null;
};

const SELF_APPLY_STORE_ITEM = 'selfApplyStore';

const StoresResponse = atom(async () => {
  const response = await secureHttpClient.getOperate<GetStoresResponse>(
    { fileName: 'storeManager.ts', fetchingFunction: 'StoresResponse' },
    `api/stores`,
  );
  addToContext('storeId', response?.defaultStore.id);
  return response;
});

const currentDefaultStoreAtom = atom<Nameable | null>(null);

export const InitialDefaultStore = atom(async (get) => {
  const defaultStore = (await get(StoresResponse)).defaultStore;
  return defaultStore;
});

const UpdatingDefaultStore = atom(
  async (get) =>
    get(currentDefaultStoreAtom) || (await get(InitialDefaultStore)),
  (_, set, v: Nameable) => set(currentDefaultStoreAtom, v),
);

export const Stores = atom(async (get) => {
  const stores = (await get(StoresResponse)).stores;
  return stores;
});

export const StoreId = atom(async (get) => {
  const defaultStore = await get(UpdatingDefaultStore);
  return defaultStore.id;
});

export const useDefaultStore = (): [
  Nameable,
  (store: Nameable) => Promise<void>,
] => {
  const [defaultStore, setDefaultStore] = useAtom(UpdatingDefaultStore);
  const userPermissions = useGetUserPermissions();
  const userProfile = useGetUserProfile();
  return [
    defaultStore,
    async (store: Nameable) => {
      if (userPermissions.StoreSelect && !userProfile?.impersonateEnabled) {
        await secureHttpClient.putOperate(
          {
            fetchingFunction: 'useDefaultStore',
            fileName: 'storeManager.ts',
          },
          'api/stores/default-store',
          {
            storeId: store.id,
          },
        );
      }
      setDefaultStore(store);
    },
  ];
};

export const useGetStores = () => useAtomValue(Stores);
export const useGetDefaultStore = () => useAtomValue(UpdatingDefaultStore);
export const useGetStoreId = () => useAtomValue(StoreId);

export const saveStoreInStorage = (store: Nameable) => {
  sessionStorage.setItem(SELF_APPLY_STORE_ITEM, JSON.stringify(store));
};

export const getStoreFromStorage = (): Nameable | null => {
  const store = sessionStorage.getItem(SELF_APPLY_STORE_ITEM);
  return store && JSON.parse(store);
};
